/**
 * F-160 D6 — surface WS reconnect state to the user.
 *
 * Subscribes to the WS client status observable; when the socket has been
 * `closed`/`connecting`/`failed` for >5s, renders a small pill at the top
 * of the viewport saying "Перепідключення…". Hides immediately once the
 * connection is back to `open` (or before the 5s grace expires).
 *
 * F-170 B1.1 — never show in the `idle` state (WS client was never asked to
 * connect, e.g. external browser context without TG initData and no auth).
 * `connecting` is treated as transient *only* once we've seen an `open` at
 * least once — the very first connect should not raise the pill, otherwise
 * every cold start flashes "Перепідключення…" before auth completes.
 *
 * No props — mounted once in `<Layout>` so the indicator follows the user
 * across every page that uses the shared layout.
 */
import { useEffect, useRef, useState } from 'react';
import { ws, type WsStatus } from '@/ws/client';
import styles from './WsStatusPill.module.css';

const OFFLINE_GRACE_MS = 5_000;

function isOffline(status: WsStatus): boolean {
  // `idle` = client never connected yet — don't pester the user.
  // `open` = healthy, also not offline.
  return status === 'closed' || status === 'connecting' || status === 'failed';
}

export function WsStatusPill(): JSX.Element | null {
  const [show, setShow] = useState(false);
  const hasBeenOpenRef = useRef(false);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const unsubscribe = ws.onStatus((status) => {
      if (status === 'open') {
        hasBeenOpenRef.current = true;
        if (timer) {
          clearTimeout(timer);
          timer = null;
        }
        setShow(false);
        return;
      }
      // Never raise the pill before the first successful connection — that
      // would flash on every cold start and on browser contexts that have
      // no token at all (idle stays idle, no pill ever).
      if (!hasBeenOpenRef.current) {
        if (timer) {
          clearTimeout(timer);
          timer = null;
        }
        setShow(false);
        return;
      }
      if (isOffline(status)) {
        if (timer) return;
        timer = setTimeout(() => {
          setShow(true);
          timer = null;
        }, OFFLINE_GRACE_MS);
      } else {
        // status === 'idle' after disconnect — silent.
        if (timer) {
          clearTimeout(timer);
          timer = null;
        }
        setShow(false);
      }
    });
    return () => {
      unsubscribe();
      if (timer) clearTimeout(timer);
    };
  }, []);

  if (!show) return null;
  return (
    <div className={styles.pill} role="status" aria-live="polite">
      <span className={styles.spinner} aria-hidden />
      Перепідключення…
    </div>
  );
}
